import { apiClient } from '@shared/api/api-client';
import type {
  ApiResponse,
  PresignedUrlRequest,
  PresignedUrlResponse,
  FileRecordRequest,
  FileRecord,
  PresignedDownloadRequest,
  PresignedDownloadResponse,
} from '@shared/types/api';

/**
 * API client for handling file storage workflows.
 */
export const filesApi = {
  /** Generate a presigned URL for file upload. */
  getPresignedUrl: async (request: PresignedUrlRequest): Promise<PresignedUrlResponse> => {
    const response = await apiClient.post<ApiResponse<PresignedUrlResponse>>(
      '/files/presigned-url',
      request
    );
    return response.data;
  },

  /** Record metadata for a successfully uploaded file. */
  recordFile: async (request: FileRecordRequest): Promise<FileRecord> => {
    const response = await apiClient.post<ApiResponse<FileRecord>>('/files/records', request);
    return response.data;
  },

  /** Generate a presigned URL for downloading a file. */
  getPresignedDownloadUrl: async (
    request: PresignedDownloadRequest
  ): Promise<PresignedDownloadResponse> => {
    const response = await apiClient.post<ApiResponse<PresignedDownloadResponse>>(
      '/files/presigned-download',
      request
    );
    return response.data;
  },

  /** Complete file upload lifecycle: presign, upload to storage, then record metadata. */
  uploadFile: async (
    file: File,
    category: string,
    options?: {
      entity_type?: string;
      entity_id?: string;
      bucket?: 'images' | 'documents';
      role?: 'patient' | 'doctor' | 'admin' | 'superadmin';
      user_email?: string;
      form_id?: string;
    }
  ): Promise<FileRecord> => {
    const presignedData = await filesApi.getPresignedUrl({
      category,
      mime_type: file.type,
      size: file.size,
      original_filename: file.name,
      entity_type: options?.entity_type,
      entity_id: options?.entity_id,
      bucket: options?.bucket,
      role: options?.role,
      user_email: options?.user_email,
      form_id: options?.form_id,
    });

    await apiClient.uploadFile(presignedData.uploadUrl, file, file.type);

    const fileRecord = await filesApi.recordFile({
      bucket_name: presignedData.bucket_name,
      storage_path: presignedData.storage_path,
      original_filename: file.name,
      mime_type: file.type,
      file_size: file.size,
      category,
      is_public: false,
      entity_type: options?.entity_type,
      entity_id: options?.entity_id,
    });

    return fileRecord;
  },
  /** Fetch metadata for a file by id. */
  getById: async (id: string): Promise<FileRecord> => {
    const response = await apiClient.get<ApiResponse<FileRecord>>(`/files/records/${id}`);
    return response.data;
  },

  /** Fetch metadata for multiple files by ids (batch operation). */
  getByIds: async (ids: string[]): Promise<FileRecord[]> => {
    if (ids.length === 0) {
      return [];
    }
    const response = await apiClient.post<ApiResponse<FileRecord[]>>('/files/records/batch', {
      file_ids: ids,
    });
    return response.data;
  },

  /** Remove a file record and delete from storage. */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/files/records/${id}`);
  },
};

export type {
  FileRecord,
  FileRecordRequest,
  PresignedDownloadRequest,
  PresignedDownloadResponse,
  PresignedUrlRequest,
  PresignedUrlResponse,
} from '@shared/types/api';
